import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import {
  getIndustryLabel,
  getSubIndustryLabel,
  getCollaborationIntentLabel,
  getCollaborationRoleLabel,
  getWhoAmILabel,
  getAdditionalTechInfraLabel,
  ProductServiceType,
  regionsData,
} from "@/components/shared/constants/general.const";
import { sanitizeTextForWinAnsiPdf } from "@/utils/pdfWinAnsiText";

const PAGE_MARGIN = 50;
const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
const CONTENT_WIDTH = PAGE_WIDTH - 2 * PAGE_MARGIN;
const BOTTOM_SAFE = 52;

const GRAY_TEXT = rgb(0.42, 0.45, 0.51);
const BLACK_TEXT = rgb(0.11, 0.11, 0.11);
const BORDER_COLOR = rgb(0.898, 0.91, 0.922);
const NAVY = rgb(4 / 255, 48 / 255, 92 / 255);
const GOLD = rgb(1, 195 / 255, 0);

function getRegionLabel(regionId: string): string {
  const region = regionsData.regions.find((r) => r.id === regionId);
  return region ? region.name : regionId;
}

function joinLabels<T>(arr: T[] | undefined, map: (x: T) => string): string {
  if (!Array.isArray(arr) || arr.length === 0) return "N/A";
  return arr.map(map).join(", ");
}

function wrapToLines(
  text: string,
  maxWidth: number,
  font: { widthOfTextAtSize: (t: string, s: number) => number },
  size: number
): string[] {
  const safe = sanitizeTextForWinAnsiPdf(text);
  const words = safe.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let currentLine = "";
  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (font.widthOfTextAtSize(testLine, size) > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine) lines.push(currentLine);
  return lines.length ? lines : ["N/A"];
}

export type AnonymizedProfilePdfCollaboration = {
  requesterRole?: string;
  expectedPartnerRole?: string;
  expressInterestTitle?: string | null;
  expressInterestSummary?: string | null;
} | null;

/**
 * PDF of the anonymized company preview.
 */
export async function downloadAnonymizedProfilePdf(
  company: Record<string, unknown>,
  collaboration?: AnonymizedProfilePdfCollaboration
): Promise<void> {
  const generatedAt = new Date();
  const pdfDoc = await PDFDocument.create();
  const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  let logoImage: Awaited<ReturnType<typeof pdfDoc.embedPng>> | null = null;
  let logoDims = { width: 0, height: 0 };
  try {
    const logoBytes = await fetch("/GT logo.png").then((r) => r.arrayBuffer());
    logoImage = await pdfDoc.embedPng(logoBytes);
    const LOGO_H = 45;
    logoDims = { width: logoImage.width * (LOGO_H / logoImage.height), height: LOGO_H };
  } catch {
    // logo unavailable – skip silently (add GT logo.png to public/ for parity with collaboration PDF)
  }

  let page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  let y = PAGE_HEIGHT - PAGE_MARGIN;

  const drawText = (
    text: string,
    x: number,
    startY: number,
    options: {
      size?: number;
      font?: typeof helvetica;
      color?: typeof BLACK_TEXT;
      maxWidth?: number;
      lineHeight?: number;
    } = {}
  ): number => {
    const {
      size = 10,
      font = helvetica,
      color = BLACK_TEXT,
      maxWidth = CONTENT_WIDTH,
      lineHeight = size * 1.5,
    } = options;

    const safe = sanitizeTextForWinAnsiPdf(text);
    const words = safe.split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let currentLine = "";

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const testWidth = font.widthOfTextAtSize(testLine, size);

      if (testWidth > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) {
      lines.push(currentLine);
    }

    let currentY = startY;
    for (const line of lines) {
      if (currentY - lineHeight < PAGE_MARGIN + BOTTOM_SAFE) {
        page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
        currentY = PAGE_HEIGHT - PAGE_MARGIN;
      }
      page.drawText(line, { x, y: currentY, size, font, color });
      currentY -= lineHeight;
    }

    return currentY;
  };

  const checkNewPage = (requiredSpace: number) => {
    if (y - requiredSpace < PAGE_MARGIN + BOTTOM_SAFE) {
      page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
      y = PAGE_HEIGHT - PAGE_MARGIN;
    }
  };

  const drawUiSectionHeader = (title: string) => {
    checkNewPage(120);
    const barH = 28;
    page.drawRectangle({ x: PAGE_MARGIN, y: y - barH, width: CONTENT_WIDTH, height: barH, color: NAVY });
    page.drawText(sanitizeTextForWinAnsiPdf(title), {
      x: PAGE_MARGIN + 10,
      y: y - barH + 9,
      size: 11,
      font: helveticaBold,
      color: GOLD,
    });
    y -= barH + 14;
  };

  const drawIntroNote = (text: string) => {
    checkNewPage(36);
    y = drawText(text, PAGE_MARGIN, y, { size: 9, color: GRAY_TEXT, maxWidth: CONTENT_WIDTH, lineHeight: 11 });
    y -= 10;
  };

  const drawProductServiceGroup = (groupTitle: string, items: Array<{ name?: string; description?: string }>) => {
    checkNewPage(32);
    page.drawText(sanitizeTextForWinAnsiPdf(groupTitle), {
      x: PAGE_MARGIN,
      y,
      size: 11,
      font: helveticaBold,
      color: BLACK_TEXT,
    });
    y -= 16;
    for (const item of items) {
      checkNewPage(48);
      page.drawText(sanitizeTextForWinAnsiPdf(item.name || "Untitled"), {
        x: PAGE_MARGIN,
        y,
        size: 10,
        font: helveticaBold,
        color: BLACK_TEXT,
      });
      y -= 14;
      y = drawText(item.description || "N/A", PAGE_MARGIN, y, {
        size: 10,
        font: helvetica,
        color: BLACK_TEXT,
        maxWidth: CONTENT_WIDTH,
        lineHeight: 14,
      });
      y -= 14;
    }
  };

  const drawBlock = (label: string, value: string) => {
    checkNewPage(56);
    page.drawText(sanitizeTextForWinAnsiPdf(label.toUpperCase()), {
      x: PAGE_MARGIN,
      y,
      size: 8,
      font: helveticaBold,
      color: GRAY_TEXT,
    });
    y -= 12;
    y = drawText(value, PAGE_MARGIN, y, {
      size: 10,
      font: helvetica,
      color: BLACK_TEXT,
      maxWidth: CONTENT_WIDTH,
    });
    y -= 20;
  };

  const GRID_COLS = 3;
  const drawLabeledGrid3 = (cells: { label: string; value: string }[]) => {
    if (cells.length === 0) return;
    const gap = 12;
    const colW = (CONTENT_WIDTH - gap * (GRID_COLS - 1)) / GRID_COLS;
    const labelSize = 7;
    const valueSize = 10;
    const valueLineH = 13;
    const labelPad = 11;

    for (let rowStart = 0; rowStart < cells.length; rowStart += GRID_COLS) {
      const rowCells = cells.slice(rowStart, rowStart + GRID_COLS);
      const layouts = rowCells.map(({ label, value }) => {
        const vLines = wrapToLines(value || "N/A", Math.max(36, colW - 4), helvetica, valueSize);
        const blockH = labelPad + vLines.length * valueLineH + 8;
        return { label, vLines, blockH };
      });
      const rowH = Math.max(44, ...layouts.map((l) => l.blockH));
      checkNewPage(rowH + 8);

      layouts.forEach((cell, colIndex) => {
        const cx = PAGE_MARGIN + colIndex * (colW + gap);
        page.drawText(sanitizeTextForWinAnsiPdf(cell.label.toUpperCase()), {
          x: cx,
          y,
          size: labelSize,
          font: helveticaBold,
          color: GRAY_TEXT,
        });
        let vy = y - labelPad;
        for (const line of cell.vLines) {
          page.drawText(line, { x: cx, y: vy, size: valueSize, font: helvetica, color: BLACK_TEXT });
          vy -= valueLineH;
        }
      });
      y -= rowH;
    }
  };

  page.drawText("Anonymized company profile", {
    x: PAGE_MARGIN,
    y,
    size: 18,
    font: helveticaBold,
    color: BLACK_TEXT,
  });
  y -= 22;
  page.drawText(
    sanitizeTextForWinAnsiPdf(
      `Generated ${generatedAt.toLocaleString(undefined, { dateStyle: "long", timeStyle: "short" })} · GTsetu`
    ),
    {
      x: PAGE_MARGIN,
      y,
      size: 9,
      font: helvetica,
      color: GRAY_TEXT,
    }
  );
  y -= 14;
  page.drawLine({
    start: { x: PAGE_MARGIN, y },
    end: { x: PAGE_WIDTH - PAGE_MARGIN, y },
    thickness: 1,
    color: BORDER_COLOR,
  });
  y -= 20;

  const gtid = company.gtid != null && String(company.gtid) !== "" ? String(company.gtid) : "N/A";
  const phoneDisplay =
    company.phone != null && String(company.phone) !== "" ? String(company.phone) : "XXXX XXXXX";

  drawUiSectionHeader("Company profile");
  drawLabeledGrid3([
    { label: "GTID", value: gtid },
    { label: "Profile status", value: company.status != null ? String(company.status) : "N/A" },
    {
      label: "Headquarters",
      value:
        company.headquartersCountry != null && String(company.headquartersCountry) !== ""
          ? String(company.headquartersCountry)
          : "N/A",
    },
    { label: "Phone", value: phoneDisplay },
    { label: "Email", value: "XXX@mail.com" },
  ]);

  const expressTitle =
    collaboration?.expressInterestTitle != null && String(collaboration.expressInterestTitle).trim() !== ""
      ? String(collaboration.expressInterestTitle).trim()
      : "";
  const expressSummary =
    collaboration?.expressInterestSummary != null && String(collaboration.expressInterestSummary).trim() !== ""
      ? String(collaboration.expressInterestSummary).trim()
      : "";
  const hasCollabIntentSection =
    collaboration?.requesterRole ||
    collaboration?.expectedPartnerRole ||
    (Array.isArray(company.collaborationIntent) && company.collaborationIntent.length > 0) ||
    expressTitle !== "" ||
    expressSummary !== "";

  if (hasCollabIntentSection) {
    drawUiSectionHeader("Collaboration Intent Details");
    drawIntroNote("Information provided by the company about their collaboration goals");
    if (expressTitle !== "") {
      drawBlock("TITLE", expressTitle);
    }
    if (expressSummary !== "") {
      drawBlock("SUMMARY", expressSummary);
    }
    if (Array.isArray(company.collaborationIntent) && company.collaborationIntent.length > 0) {
      drawBlock(
        "COLLABORATION TYPE THEY'RE EXPLORING",
        (company.collaborationIntent as string[]).map((i) => getCollaborationIntentLabel(i)).join(", ")
      );
    }
    if (collaboration?.requesterRole) {
      drawBlock("THEIR ROLE IN THIS COLLABORATION", getCollaborationRoleLabel(collaboration.requesterRole));
    }
    if (collaboration?.expectedPartnerRole) {
      drawBlock("WHAT THEY EXPECT FROM YOU", getCollaborationRoleLabel(collaboration.expectedPartnerRole));
    }
  }

  drawUiSectionHeader("Business Overview");
  drawBlock(
    "COMPANY DESCRIPTION",
    company.description != null && String(company.description) !== "" ? String(company.description) : "N/A"
  );
  drawBlock(
    "SUB DESCRIPTION",
    company.subDescription != null && String(company.subDescription) !== ""
      ? String(company.subDescription)
      : "N/A"
  );
  const operations = Array.isArray(company.operations) ? (company.operations as string[]) : [];
  drawBlock("OPERATIONS", operations.length > 0 ? operations.join(", ") : "N/A");
  drawLabeledGrid3([
    { label: "Trade name", value: "XXXX" },
    {
      label: "Industry category",
      value: joinLabels(company.industryCategory as string[] | undefined, (c) => getIndustryLabel(c)),
    },
    {
      label: "Sub industry",
      value: joinLabels(company.subIndustry as string[] | undefined, (s) => getSubIndustryLabel(s)),
    },
    {
      label: "Who am I",
      value: joinLabels(company.whoAmI as string[] | undefined, (r) => getWhoAmILabel(r)),
    },
    {
      label: "Collaboration intent",
      value: joinLabels(company.collaborationIntent as string[] | undefined, (i) => getCollaborationIntentLabel(i)),
    },
  ]);

  const productServices = (company.productServices as Array<{ name?: string; description?: string; type: string }>) || [];
  const products = productServices.filter((p) => p.type === ProductServiceType.PRODUCT);
  const services = productServices.filter((p) => p.type === ProductServiceType.SERVICE);

  drawUiSectionHeader("Products & Services");
  if (products.length === 0 && services.length === 0) {
    checkNewPage(24);
    page.drawText("N/A", { x: PAGE_MARGIN, y, size: 11, font: helvetica, color: GRAY_TEXT });
    y -= 28;
  } else {
    if (products.length > 0) {
      drawProductServiceGroup("PRODUCTS", products);
    }
    if (services.length > 0) {
      drawProductServiceGroup("SERVICES", services);
    }
  }

  drawUiSectionHeader("Capabilities & Offerings");
  const core = (company.coreCapabilities as string[]) || [];
  drawBlock("CORE CAPABILITIES", core.length > 0 ? core.join(", ") : "N/A");
  const secondary = (company.secondaryCapabilities as string[]) || [];
  if (secondary.length > 0) {
    drawBlock("SECONDARY CAPABILITIES", secondary.join(", "));
  }
  if (company.keyDifferentiators != null && String(company.keyDifferentiators) !== "") {
    drawBlock("KEY DIFFERENTIATORS", String(company.keyDifferentiators));
  }
  const tech = (company.technologies as string[]) || [];
  drawBlock("TECHNOLOGY & INFRASTRUCTURE", tech.length > 0 ? tech.join(", ") : "N/A");
  const addTech = (company.additionalTechInfrastructure as string[]) || [];
  if (addTech.length > 0) {
    drawBlock(
      "ADDITIONAL TECHNOLOGY & INFRASTRUCTURE",
      addTech.map((item) => getAdditionalTechInfraLabel(item)).join(", ")
    );
  }

  drawUiSectionHeader("Geography & Markets");
  const op = (company.operatingCountries as string[]) || [];
  const pref = (company.preferredCollaborationRegions as string[]) || [];
  drawLabeledGrid3([
    { label: "Operating geographies", value: op.length > 0 ? op.join(", ") : "N/A" },
    {
      label: "Preferred collaboration regions",
      value: pref.length > 0 ? pref.map((r) => getRegionLabel(r)).join(", ") : "N/A",
    },
  ]);

  drawUiSectionHeader("Contact Person");
  drawLabeledGrid3([
    { label: "Name", value: "XXXX" },
    { label: "Phone number", value: "XXXX" },
    { label: "Designation", value: "XXXX" },
  ]);

  const logs = company.externalVerificationLogs as Array<{ metadata?: Record<string, unknown> }> | undefined;
  const metadata = logs?.[0]?.metadata;
  if (metadata && typeof metadata === "object") {
    drawUiSectionHeader("System Verification Details");
    const mask = (v: unknown) => (v != null && String(v) !== "" ? "XXXX" : "-");
    drawLabeledGrid3([
      { label: "Company name", value: mask(metadata.companyName) },
      { label: "Company address", value: mask(metadata.companyAddress) },
      { label: "Date of registration of certificate", value: mask(metadata.dateOfIncorporation) },
      { label: "Registration number", value: mask(metadata.registrationNumber) },
      { label: "Company type", value: mask(metadata.companyType) },
      { label: "Company status", value: mask(metadata.companyStatus) },
    ]);
  }

  drawUiSectionHeader("About Verified Listings");
  const verifiedBullets = [
    "Full company details are visible after mutual interest is confirmed",
    "Verification badge confirms company authenticity",
    "Contact information becomes accessible to matched companies",
    "Both parties can initiate collaboration discussions",
  ];
  for (const line of verifiedBullets) {
    checkNewPage(28);
    y = drawText(`• ${line}`, PAGE_MARGIN, y, { size: 10, maxWidth: CONTENT_WIDTH - 8, lineHeight: 14 });
    y -= 6;
  }
  y -= 10;

  const pages = pdfDoc.getPages();
  const totalPages = pages.length;
  pages.forEach((p, idx) => {
    if (logoImage) {
      p.drawImage(logoImage, {
        x: PAGE_WIDTH - PAGE_MARGIN - logoDims.width,
        y: PAGE_HEIGHT - PAGE_MARGIN + 5,
        width: logoDims.width,
        height: logoDims.height,
      });
    }
    const pageLabel = `${idx + 1} / ${totalPages}`;
    p.drawText(pageLabel, {
      x: PAGE_WIDTH - PAGE_MARGIN - helvetica.widthOfTextAtSize(pageLabel, 8),
      y: 20,
      size: 8,
      font: helvetica,
      color: GRAY_TEXT,
    });
    p.drawText("GTsetu – Anonymized Profile Preview – Confidential", {
      x: PAGE_MARGIN,
      y: 20,
      size: 7,
      font: helvetica,
      color: GRAY_TEXT,
    });
  });

  const bytes = await pdfDoc.save();
  const blob = new Blob([bytes as unknown as BlobPart], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const stamp = generatedAt.toISOString().slice(0, 19).replace(/[:T]/g, "-");
  const safeGtid = gtid.replace(/[^a-zA-Z0-9-_]/g, "").slice(0, 24) || "profile";
  const a = document.createElement("a");
  a.href = url;
  a.download = `anonymized-profile-${safeGtid}-${stamp}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}
